/**
 * Integrated curriculum financial planning — PROTOTYPE.
 *
 * ═══════════════════════════════════════════════════════════════════
 * THIS IS A PROTOTYPE AND THE SCREEN SAYS SO IN SO MANY WORDS.
 *
 * It is a deterministic model over stated assumptions with a published
 * arithmetic basis. It is NOT validated against any published benchmark, it is
 * NOT a substitute for a dedicated curriculum planning product, it has no
 * knowledge of timetables, subjects, sets or rooms, and it must not be the sole
 * basis for a staffing decision.
 *
 * The calculation is printed on the page. Anybody can check the arithmetic
 * against the figures, which is the least a planning tool owes its reader.
 * ═══════════════════════════════════════════════════════════════════
 */

import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync, useMutation } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { useTerms } from '../lib/tenant';
import { money, percent } from '../lib/format';
import { Alert, AsyncPanel, Badge, Card, DataTable, EmptyState, ErrorNotice, Money, PageHeader, Stat } from '../components/ui';

interface ScenarioRow {
  id: string;
  code: string;
  name: string;
  description: string | null;
  horizon_years: number;
  status: string;
  source: string;
  is_prototype: boolean;
  entity_code: string | null;
  entity_count: string;
}

interface ScenarioView {
  scenario: Record<string, unknown> & { id: string; code: string; name: string; description: string | null; horizon_years: number; is_prototype: boolean; created_by_name: string | null };
  entities: Array<{
    entityId: string;
    entityCode: string;
    entityName: string;
    years: Array<{
      yearOffset: number;
      incomeMinor: number;
      payCostMinor: number;
      nonPayCostMinor: number;
      surplusMinor: number;
      payAsPercentOfIncome: string;
      pupilTeacherRatio: string;
    }>;
    assumptions: Array<{
      yearOffset: number;
      pupilsOnRoll: number;
      teachingFte: number;
      supportFte: number;
      leadershipFte: number;
      averageTeacherCostMinor: number;
      fundingPerPupilMinor: number;
      payAwardPercent: number;
    }>;
  }>;
  totals: Array<{
    yearOffset: number;
    incomeMinor: number;
    payCostMinor: number;
    nonPayCostMinor: number;
    surplusMinor: number;
    payAsPercentOfIncome: string;
  }>;
  isPrototype: boolean;
  calculationBasis: string;
  notice: string;
}

export function PlanningPage() {
  const t = useTerms();
  const { can } = useSession();
  const [selected, setSelected] = useState<string | null>(null);

  const scenarios = useAsync(
    () => api.get<{ scenarios: ScenarioRow[]; notice: string; calculationBasis: string }>('/platform/planning/scenarios'),
    [],
  );

  const view = useAsync(
    () =>
      selected
        ? api.get<ScenarioView>(`/platform/planning/scenarios/${selected}`)
        : Promise.resolve(null as unknown as ScenarioView),
    [selected],
  );

  const remodel = useMutation(async (scenarioId: string) =>
    api.post<{ years: number; entities: number }>(`/platform/planning/scenarios/${scenarioId}/remodel`, {}),
  );

  return (
    <>
      <PageHeader
        title="Curriculum financial planning"
        subtitle="A three-year model of the pressure between funding and pay."
        meta={<Badge tone="warn">Prototype</Badge>}
      />

      <Alert tone="warn" title="This is a prototype">
        <p>
          It is a deterministic model over stated assumptions, and its arithmetic is published below. It has not been
          validated against any published benchmark, it knows nothing about timetables, subjects, sets or rooms, and it
          is not a substitute for a dedicated curriculum planning product. Use it to see the shape of a pressure — not
          to set a staffing establishment on its own.
        </p>
      </Alert>

      <AsyncPanel state={scenarios}>
        {(data) =>
          data.scenarios.length === 0 ? (
            <EmptyState title="No planning scenarios have been created" />
          ) : (
            <div className="stack">
              <Card title="Scenarios" flush>
                <DataTable
                  caption="Planning scenarios"
                  rows={data.scenarios}
                  rowKey={(s) => s.id}
                  onRowClick={(s) => setSelected(s.id)}
                  columns={[
                    { key: 'code', header: 'Code', render: (s) => <span className="mono">{s.code}</span> },
                    {
                      key: 'name',
                      header: 'Scenario',
                      render: (s) => (
                        <>
                          <div>{s.name}</div>
                          {s.description ? <div className="smaller muted">{s.description}</div> : null}
                        </>
                      ),
                    },
                    { key: 'horizon', header: 'Horizon', render: (s) => `${s.horizon_years} years` },
                    { key: 'entities', header: t.entityPlural, numeric: true, render: (s) => s.entity_count },
                    {
                      key: 'status',
                      header: 'Status',
                      render: (s) => (
                        <>
                          <Badge tone="neutral">{s.status}</Badge>{' '}
                          {s.is_prototype ? <Badge tone="warn">Prototype</Badge> : null}
                        </>
                      ),
                    },
                    {
                      key: 'open',
                      header: <span className="visually-hidden">Open</span>,
                      render: (s) => (
                        <button type="button" className="btn btn--sm" onClick={() => setSelected(s.id)}>
                          Open
                        </button>
                      ),
                    },
                  ]}
                />
              </Card>

              {selected ? (
                <AsyncPanel state={view}>
                  {(scenario) =>
                    scenario ? (
                      <div className="stack">
                        <Card
                          title={scenario.scenario.name}
                          subtitle={scenario.scenario.description ?? undefined}
                          actions={
                            can('plan.manage') ? (
                              <button
                                type="button"
                                className="btn btn--sm"
                                disabled={remodel.busy}
                                onClick={() => void remodel.run(selected).then(() => view.reload())}
                              >
                                {remodel.busy ? 'Re-modelling…' : 'Re-model'}
                              </button>
                            ) : null
                          }
                        >
                          <ErrorNotice error={remodel.error} />
                          <div className="grid grid--3">
                            {scenario.totals.map((total) => (
                              <Stat
                                key={total.yearOffset}
                                label={total.yearOffset === 0 ? 'This year' : `Year ${total.yearOffset + 1}`}
                                value={money(total.surplusMinor)}
                                tone={total.surplusMinor >= 0 ? 'positive' : 'negative'}
                                detail={
                                  <>
                                    Income {money(total.incomeMinor)}
                                    <br />
                                    Pay {total.payAsPercentOfIncome}% of income
                                  </>
                                }
                              />
                            ))}
                          </div>
                          <p className="smaller muted" style={{ marginTop: '1rem' }}>
                            <strong>How these figures are calculated.</strong> {scenario.calculationBasis}
                          </p>
                        </Card>

                        <Card title={`By ${t.entity.toLowerCase()}, year one`} flush>
                          <DataTable
                            caption={`Modelled position for each ${t.entity.toLowerCase()}`}
                            rows={scenario.entities}
                            rowKey={(e) => e.entityId}
                            columns={[
                              { key: 'code', header: 'Code', render: (e) => <span className="mono">{e.entityCode}</span> },
                              { key: 'name', header: t.entity, render: (e) => <span className="truncate">{e.entityName}</span> },
                              {
                                key: 'pupils',
                                header: 'On roll',
                                numeric: true,
                                render: (e) => e.assumptions[0]?.pupilsOnRoll ?? '—',
                              },
                              {
                                key: 'income',
                                header: 'Income',
                                numeric: true,
                                render: (e) => <Money minor={e.years[0]?.incomeMinor ?? 0} />,
                              },
                              {
                                key: 'pay',
                                header: 'Pay',
                                numeric: true,
                                render: (e) => <Money minor={e.years[0]?.payCostMinor ?? 0} />,
                              },
                              {
                                key: 'paypc',
                                header: 'Pay % of income',
                                numeric: true,
                                render: (e) => {
                                  const value = Number(e.years[0]?.payAsPercentOfIncome ?? 0);
                                  return (
                                    <span className={value > 80 ? 'num--negative' : undefined}>
                                      {e.years[0]?.payAsPercentOfIncome ?? '—'}%
                                    </span>
                                  );
                                },
                              },
                              {
                                key: 'ptr',
                                header: 'Pupil : teacher',
                                numeric: true,
                                render: (e) => e.years[0]?.pupilTeacherRatio ?? '—',
                              },
                              {
                                key: 'surplus',
                                header: 'Surplus',
                                numeric: true,
                                render: (e) => <Money minor={e.years[0]?.surplusMinor ?? 0} />,
                              },
                              {
                                key: 'y3',
                                header: 'Surplus, year 3',
                                numeric: true,
                                render: (e) => <Money minor={e.years[e.years.length - 1]?.surplusMinor ?? 0} />,
                              },
                            ]}
                            footer={
                              <tr className="is-total">
                                <td colSpan={3}>Total</td>
                                <td className="num">{money(scenario.totals[0]?.incomeMinor ?? 0)}</td>
                                <td className="num">{money(scenario.totals[0]?.payCostMinor ?? 0)}</td>
                                <td className="num">{scenario.totals[0]?.payAsPercentOfIncome ?? '—'}%</td>
                                <td />
                                <td className="num">{money(scenario.totals[0]?.surplusMinor ?? 0)}</td>
                                <td className="num">
                                  {money(scenario.totals[scenario.totals.length - 1]?.surplusMinor ?? 0)}
                                </td>
                              </tr>
                            }
                          />
                        </Card>

                        <Card title="What the model assumes" subtitle="Change an assumption and the figures follow.">
                          <DataTable
                            caption="Assumptions, year one"
                            rows={scenario.entities}
                            rowKey={(e) => `${e.entityId}-assumptions`}
                            columns={[
                              { key: 'code', header: 'Code', render: (e) => <span className="mono">{e.entityCode}</span> },
                              { key: 'pupils', header: 'Pupils', numeric: true, render: (e) => e.assumptions[0]?.pupilsOnRoll ?? '—' },
                              { key: 'teach', header: 'Teaching FTE', numeric: true, render: (e) => e.assumptions[0]?.teachingFte ?? '—' },
                              { key: 'lead', header: 'Leadership FTE', numeric: true, render: (e) => e.assumptions[0]?.leadershipFte ?? '—' },
                              { key: 'support', header: 'Support FTE', numeric: true, render: (e) => e.assumptions[0]?.supportFte ?? '—' },
                              {
                                key: 'cost',
                                header: 'Average teacher cost',
                                numeric: true,
                                render: (e) => <Money minor={e.assumptions[0]?.averageTeacherCostMinor ?? 0} />,
                              },
                              {
                                key: 'funding',
                                header: 'Funding per pupil',
                                numeric: true,
                                render: (e) => <Money minor={e.assumptions[0]?.fundingPerPupilMinor ?? 0} />,
                              },
                              {
                                key: 'award',
                                header: 'Pay award',
                                numeric: true,
                                render: (e) => percent(e.assumptions[0]?.payAwardPercent ?? 0),
                              },
                            ]}
                          />
                        </Card>
                      </div>
                    ) : null
                  }
                </AsyncPanel>
              ) : null}
            </div>
          )
        }
      </AsyncPanel>
    </>
  );
}
